import React, { useState } from 'react';
import { tokens } from '../theme/tokens';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  Visibility,
  VisibilityOff,
  VpnKey,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userManagementApi, ApiKey } from '../services/userManagementApi';
import {
  ApiKeyPermissionPicker,
  DEFAULT_STORE_API_KEY_PERMISSIONS,
  getPermissionsChipTooltip,
} from './ApiKeyPermissionPicker';
import { ConfirmDialog, EmptyState, FormDialog, LoadingState, StatusChip } from './ui';
import { apiService } from '../services/api';
import { ApiKeyDataScopePicker } from './ApiKeyDataScopePicker';
import { ApiKeyDataScope, describeKeyDataScope } from '../types/apiKeyDataScope';

interface AdminUserApiKeysDialogProps {
  open: boolean;
  userId: string;
  userLabel: string;
  onClose: () => void;
}

const maskApiKey = (key: string) => {
  if (!key || key.length < 12) return '••••••••';
  return key.substring(0, 8) + '•'.repeat(Math.max(4, key.length - 12)) + key.substring(key.length - 4);
};

export const AdminUserApiKeysDialog: React.FC<AdminUserApiKeysDialogProps> = ({
  open,
  userId,
  userLabel,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editKey, setEditKey] = useState<ApiKey | null>(null);
  const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);
  const [keyName, setKeyName] = useState('');
  const [permissions, setPermissions] = useState<string[]>([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null
  );
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  const queryKey = ['admin-user-api-keys', userId];

  const { data: apiKeys = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => userManagementApi.getUserApiKeys(userId),
    enabled: open && !!userId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  // Backend flag `scoped_api_keys`, evaluated for the target user's organization.
  const { data: scopeOptions = null } = useQuery({
    queryKey: ['admin-user-api-key-scope-options', userId],
    queryFn: () => apiService.getApiKeyScopeOptions(userId),
    enabled: open && !!userId,
    retry: false,
  });
  const [dataScope, setDataScope] = useState<ApiKeyDataScope | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      userManagementApi.createApiKey(userId, {
        name: keyName.trim(),
        permissions,
        ...(scopeOptions?.enabled && dataScope ? { dataScope } : {}),
      }),
    onSuccess: (key) => {
      setCreatedSecret(key.key);
      setCreateOpen(false);
      setKeyName('');
      setPermissions([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
      setFeedback({ severity: 'success', message: 'API key created. Copy it now — it may not be shown again.' });
      invalidate();
    },
    onError: (err: Error) => {
      setFeedback({ severity: 'error', message: err.message || 'Failed to create API key' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editKey) throw new Error('No key selected');
      return userManagementApi.updateApiKey(editKey.id, {
        name: keyName.trim(),
        permissions,
        // null clears the scope (the key inherits the organization again).
        ...(scopeOptions?.enabled ? { dataScope } : {}),
      });
    },
    onSuccess: () => {
      setEditKey(null);
      setFeedback({ severity: 'success', message: 'API key updated.' });
      invalidate();
    },
    onError: (err: Error) => {
      setFeedback({ severity: 'error', message: err.message || 'Failed to update API key' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (apiKeyId: string) => userManagementApi.deleteApiKey(apiKeyId),
    onSuccess: () => {
      setDeleteKeyId(null);
      setFeedback({ severity: 'success', message: 'API key deleted.' });
      invalidate();
    },
    onError: (err: Error) => {
      setFeedback({ severity: 'error', message: err.message || 'Failed to delete API key' });
    },
  });

  const openCreate = () => {
    setKeyName('');
    setPermissions([...DEFAULT_STORE_API_KEY_PERMISSIONS]);
    setDataScope(null);
    setCreatedSecret(null);
    setCreateOpen(true);
  };

  const openEdit = (key: ApiKey) => {
    setEditKey(key);
    setDataScope(key.dataScope || null);
    setKeyName(key.name || '');
    setPermissions(
      key.permissions && key.permissions.length > 0
        ? [...key.permissions]
        : [...DEFAULT_STORE_API_KEY_PERMISSIONS]
    );
  };

  const toggleVisible = (id: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setFeedback({ severity: 'success', message: 'Copied to clipboard.' });
    } catch {
      setFeedback({ severity: 'error', message: 'Failed to copy.' });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VpnKey />
            <Box>
              <Typography variant="h6">API Keys</Typography>
              <Typography variant="body2" color="text.secondary">
                {userLabel}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {feedback && (
            <Alert
              severity={feedback.severity}
              sx={{ mb: 2 }}
              onClose={() => setFeedback(null)}
            >
              {feedback.message}
            </Alert>
          )}
          {createdSecret && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => copyText(createdSecret)}>
                  Copy
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontFamily: tokens.font.mono, wordBreak: 'break-all' }}>
                {createdSecret}
              </Typography>
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button startIcon={<Add />} variant="contained" size="small" onClick={openCreate}>
              Create key
            </Button>
          </Stack>

          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <Alert severity="error">
              {(error as Error).message || 'Failed to load API keys'}
            </Alert>
          ) : apiKeys.length === 0 ? (
            <EmptyState size="compact" title="No API keys for this user yet." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Permissions</TableCell>
                    {scopeOptions?.enabled && <TableCell>Data scope</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((key) => {
                    const perms = key.permissions || [];
                    return (
                      <TableRow key={key.id}>
                        <TableCell>{key.name}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontFamily="monospace">
                              {visibleKeys.has(key.id) ? key.key : maskApiKey(key.key)}
                            </Typography>
                            <IconButton size="small" onClick={() => toggleVisible(key.id)}>
                              {visibleKeys.has(key.id) ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton size="small" onClick={() => copyText(key.key)}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {perms.length === 0 ? (
                            <Tooltip title={getPermissionsChipTooltip(perms)}>
                              <Chip label="None (blocked)" size="small" color="warning" />
                            </Tooltip>
                          ) : perms.includes('*') ? (
                            <Tooltip title={getPermissionsChipTooltip(perms)}>
                              <Chip label="Full access" size="small" color="warning" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={getPermissionsChipTooltip(perms)}>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {perms.slice(0, 2).map((p) => (
                                  <Chip key={p} label={p} size="small" variant="outlined" />
                                ))}
                                {perms.length > 2 && (
                                  <Chip label={`+${perms.length - 2}`} size="small" />
                                )}
                              </Stack>
                            </Tooltip>
                          )}
                        </TableCell>
                        {scopeOptions?.enabled && (
                          <TableCell sx={{ maxWidth: 220 }}>{describeKeyDataScope(key.dataScope)}</TableCell>
                        )}
                        <TableCell>
                          <StatusChip
                            label={key.isActive ? 'Active' : 'Inactive'}
                            tone={key.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(key)} aria-label="Edit key">
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteKeyId(key.id)}
                            aria-label="Delete key"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <FormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={() => createMutation.mutate()}
        title="Create API Key for User"
        submitLabel={createMutation.isPending ? 'Creating...' : 'Create'}
        submitDisabled={!keyName.trim() || permissions.length === 0 || createMutation.isPending}
        maxWidth="md"
      >
        <TextField
          autoFocus
          margin="dense"
          label="Key Name"
          fullWidth
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
          placeholder="e.g., Impulse metadata writer"
          sx={{ mb: 2, mt: 1 }}
        />
        <ApiKeyPermissionPicker value={permissions} onChange={setPermissions} />
        <ApiKeyDataScopePicker value={dataScope} onChange={setDataScope} options={scopeOptions} />
      </FormDialog>

      <FormDialog
        open={editKey !== null}
        onClose={() => setEditKey(null)}
        onSubmit={() => updateMutation.mutate()}
        title="Edit API Key Permissions"
        submitLabel={updateMutation.isPending ? 'Saving...' : 'Save'}
        submitDisabled={!keyName.trim() || permissions.length === 0 || updateMutation.isPending}
        maxWidth="md"
      >
        <TextField
          autoFocus
          margin="dense"
          label="Key Name"
          fullWidth
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <ApiKeyPermissionPicker value={permissions} onChange={setPermissions} />
        <ApiKeyDataScopePicker value={dataScope} onChange={setDataScope} options={scopeOptions} />
      </FormDialog>

      <ConfirmDialog
        open={deleteKeyId !== null}
        title="Delete API Key"
        message="Delete this API key? The store will lose access immediately."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteKeyId) deleteMutation.mutate(deleteKeyId);
        }}
        onClose={() => setDeleteKeyId(null)}
      />
    </>
  );
};
